import { useEffect, useState } from "react";
import crudApi from "@serie3/common/api/crudApi";
import UserConfig from "@serie3/mymediaplanner/Modules/User/Resources/Endpoints";

/**
 * A user's profile picture, or their initials when they have none.
 *
 * Images come from an authenticated endpoint, so they are fetched as blobs and
 * shown through object URLs. Results are cached per user for the page's
 * lifetime: a dropdown re-rendering ten times must not fetch ten times.
 * After the current user changes their own picture call invalidateAvatar("me").
 */

const COLORS = [
  "#6aabce", "#6dbf99", "#d4906e", "#8c82c4",
  "#c9aa52", "#5db8cc", "#c47a9e", "#c98f50",
];

const cache = new Map(); // key -> Promise<string|null>

const fetchAvatar = async (endpoint) => {
  try {
    const response = await crudApi.getBinary(endpoint);
    if (response.status !== 200 || !response.data || response.data.size === 0) return null;
    return window.URL.createObjectURL(response.data);
  } catch {
    return null;
  }
};

export const avatarKey = (userId) => (userId === "me" ? "me" : String(userId));

export const invalidateAvatar = (key) => {
  const pending = cache.get(key);
  cache.delete(key);
  if (pending) pending.then((url) => { if (url) window.URL.revokeObjectURL(url); });
};

/** url | null (none) | undefined (loading) */
export const useAvatar = ({ userId, hasPicture = true, version = 0 }) => {
  const key = avatarKey(userId);
  const [url, setUrl] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!hasPicture || userId === undefined || userId === null || userId === "") {
      setUrl(null);
      return undefined;
    }
    if (!cache.has(key)) {
      const endpoint = key === "me"
        ? UserConfig.endpoints.profilePicture.me
        : UserConfig.endpoints.profilePicture.user + key;
      cache.set(key, fetchAvatar(endpoint));
    }
    setUrl(undefined);
    cache.get(key).then((result) => { if (!cancelled) setUrl(result); });
    return () => { cancelled = true; };
  }, [key, userId, hasPicture, version]);

  return url;
};

export const initialsOf = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
};

const colorFor = (userId, name) => {
  const n = Number(userId);
  const seed = Number.isFinite(n) ? n : String(name || "").length;
  return COLORS[Math.abs(seed) % COLORS.length];
};

const UserAvatar = ({ userId, name, size = 28, hasPicture = true, version = 0, style = {} }) => {
  const url = useAvatar({ userId, hasPicture, version });
  const base = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    overflow: "hidden",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...style,
  };

  if (url) {
    return (
      <span style={base} aria-hidden="true">
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </span>
    );
  }

  return (
    <span
      style={{
        ...base,
        background: colorFor(userId, name),
        color: "#fff",
        fontSize: Math.max(10, Math.round(size * 0.38)),
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </span>
  );
};

export default UserAvatar;
